import { DOCUMENT } from '@angular/common';
import { Directive, ElementRef, NgZone, OnDestroy, OnInit, Renderer2, inject } from '@angular/core';

@Directive({
  selector: '[talyAppStickyFooter]',
  standalone: false,
})
export class StickyFooterDirective implements OnInit, OnDestroy {
  private readonly el = inject(ElementRef<HTMLElement>);
  private readonly renderer = inject(Renderer2);
  private readonly zone = inject(NgZone);
  private readonly document = inject(DOCUMENT);

  private isMonitoringStarted = false;
  private initialViewportHeight = 0;
  private readonly keyboardOpenThreshold = 100;
  private focusOutTimer: ReturnType<typeof setTimeout> | null = null;
  private viewportHandler: (() => void) | null = null;
  private orientationHandler: (() => void) | null = null;
  private focusInHandler: ((event: FocusEvent) => void) | null = null;
  private focusOutHandler: (() => void) | null = null;

  ngOnInit(): void {
    if (typeof window === 'undefined' || !this.isMobileDevice()) {
      return;
    }

    this.isMonitoringStarted = true;
    this.renderer.addClass(this.el.nativeElement, 'keyboard-closed');
    this.renderer.setStyle(this.el.nativeElement, '--taly-keyboard-height', '0px');

    this.zone.runOutsideAngular(() => {
      if (window.visualViewport) {
        this.initialViewportHeight = window.visualViewport.height;
        this.viewportHandler = () => this.handleViewportChange();
        this.orientationHandler = () => this.resetViewportBaseline();

        window.visualViewport.addEventListener('resize', this.viewportHandler);
        window.visualViewport.addEventListener('scroll', this.viewportHandler);
        window.addEventListener('orientationchange', this.orientationHandler);
      }

      this.focusInHandler = (event: FocusEvent) => this.handleFocusIn(event);
      this.focusOutHandler = () => this.handleFocusOut();
      this.document.addEventListener('focusin', this.focusInHandler);
      this.document.addEventListener('focusout', this.focusOutHandler);
    });
  }

  ngOnDestroy(): void {
    if (!this.isMonitoringStarted || typeof window === 'undefined') {
      return;
    }

    this.isMonitoringStarted = false;

    if (this.focusOutTimer) {
      clearTimeout(this.focusOutTimer);
      this.focusOutTimer = null;
    }

    if (window.visualViewport && this.viewportHandler) {
      window.visualViewport.removeEventListener('resize', this.viewportHandler);
      window.visualViewport.removeEventListener('scroll', this.viewportHandler);
    }

    if (this.orientationHandler) {
      window.removeEventListener('orientationchange', this.orientationHandler);
    }

    if (this.focusInHandler) {
      this.document.removeEventListener('focusin', this.focusInHandler);
    }

    if (this.focusOutHandler) {
      this.document.removeEventListener('focusout', this.focusOutHandler);
    }
  }

  private handleViewportChange(): void {
    if (!window.visualViewport) {
      return;
    }

    const viewport = window.visualViewport;
    this.initialViewportHeight = Math.max(this.initialViewportHeight, viewport.height);

    const keyboardHeight = Math.max(
      0,
      Math.round(this.initialViewportHeight - viewport.height - viewport.offsetTop)
    );
    this.updateElementState(keyboardHeight > this.keyboardOpenThreshold, keyboardHeight);
  }

  private resetViewportBaseline(): void {
    if (!window.visualViewport) {
      return;
    }

    this.initialViewportHeight = window.visualViewport.height;
    this.updateElementState(false, 0);
  }

  private handleFocusIn(event: FocusEvent): void {
    if (!this.isMonitoringStarted) {
      return;
    }

    if (this.focusOutTimer) {
      clearTimeout(this.focusOutTimer);
      this.focusOutTimer = null;
    }

    if (this.isTextInput(event.target) && !window.visualViewport) {
      this.updateElementState(true, 0);
    }
  }

  private handleFocusOut(): void {
    if (!this.isMonitoringStarted) {
      return;
    }

    if (this.focusOutTimer) {
      clearTimeout(this.focusOutTimer);
    }

    this.focusOutTimer = setTimeout(() => {
      if (!window.visualViewport) {
        this.updateElementState(false, 0);
      }
      this.focusOutTimer = null;
    }, 120);
  }

  private updateElementState(isKeyboardOpen: boolean, keyboardHeight: number): void {
    const element = this.el.nativeElement;

    this.renderer.setStyle(element, '--taly-keyboard-height', `${keyboardHeight}px`);

    if (isKeyboardOpen) {
      this.renderer.addClass(element, 'keyboard-open');
      this.renderer.setStyle(element, 'position', 'relative');

      this.renderer.removeClass(element, 'keyboard-closed');
      return;
    }

    this.renderer.removeClass(element, 'keyboard-open');
    this.renderer.setStyle(element, 'position', 'fixed');
    this.renderer.addClass(element, 'keyboard-closed');
  }

  private isTextInput(target: EventTarget | null): boolean {
    const element = target as HTMLElement | null;
    if (!element) {
      return false;
    }

    if (element.tagName === 'TEXTAREA') {
      return true;
    }

    if (element.tagName === 'INPUT') {
      const input = element as HTMLInputElement;
      return input.type !== 'checkbox' && input.type !== 'radio' && input.type !== 'button';
    }

    return element.isContentEditable;
  }

  private isMobileDevice(): boolean {
    const userAgent = navigator.userAgent || '';
    const touchMac = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
    return /Android|iPhone|iPad|iPod/i.test(userAgent) || touchMac;
  }
}
